// Meridian component imports
import React, { useEffect, useRef, useState } from 'react';

import Button from '@amzn/meridian/button';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import { type SelectOptionProps } from '@amzn/meridian/select/select-option';
import Text from '@amzn/meridian/text';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import ResponsiveWidthBox from 'src/components/shared/responsiveWidthBox/responsiveWidthBox';
// Shared project imports
import { COUNTRY_ID_MAPPING } from 'src/config/CountryIdMapping';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';

import { WEBHOOK_EVENT_ID_LIST } from './constants';
// Webhook specific imports
import './siteWebhooks.scss';
import {
  indexAndSelectNewOrUpdatedWebhooks,
  setAustinAccommodationTypeOnRelevantEventIds,
  validateWebhook,
  webhookFormEntryToPostObject,
} from './siteWebhookUtils';
import { ACCOMMODATION_TYPE_IDS, type SiteWebhook, type WEBHOOK_EVENT_IDS } from './types';
import { type IndexedSiteWebhookFormEntry, type SiteWebhookFormEntry, type WebhookFormErrors } from './types';

import WebhookEventCard, { type WebhookEventCardProps } from './components/webhookEventCard';
import webHookService from './service/webHookService';

const SiteWebHooks = () => {
  const dispatch = useAppDispatch();
  const { selectedSite } = useAppSelector((state) => state.sites);

  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<WebhookFormErrors>({});
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(true);

  /** Readonly state of webhooks received from backend */
  const [loadedSiteWebhooks, setLoadedSiteWebhooks] = useState<SiteWebhook[]>([]);
  /** Workspace for the display of ongoing creation, editing, and deletion of site webhooks */
  const [siteWebhookFormEntries, setSiteWebhookFormEntries] = useState<SiteWebhookFormEntry[]>([]);
  /** Accumulates IDs of webhooks that came from backend and need an API call to persist deletion */
  const webhookIdsToDelete = useRef<number[]>([]);

  // handle initial load and keep updated if site selection changes
  useEffect(() => {
    if (!!selectedSite && !!selectedSite.id) {
      fetchWebhooks();
    }
  }, [selectedSite]);

  const fetchWebhooks = async () => {
    setShowLoadingSpinner(true);
    // clear editing state if the site selection changes or we load new webhook data after successful post
    setIsEditing(false);
    try {
      const siteWebhooks = await webHookService.getSiteWebhooks(selectedSite.id);
      setLoadedSiteWebhooks(siteWebhooks);
    } finally {
      setShowLoadingSpinner(false);
    }
  };

  const toggleEditing = () => {
    // reset working state when finished editing
    webhookIdsToDelete.current = [];
    if (isEditing) {
      setErrors({});
      setSiteWebhookFormEntries([]);
    } else {
      initializeWebhookFormEntries();
    }
    setIsEditing(!isEditing);
  };

  const onSave = async () => {
    // map to include overall index so errors can be associated with the correct fields
    // exclude unchanged existing webhooks and new blank rows from updates
    const validationTargets = indexAndSelectNewOrUpdatedWebhooks(siteWebhookFormEntries);

    // set austin accommodation type for austin only events (note: must happen after blank row check)
    setAustinAccommodationTypeOnRelevantEventIds(validationTargets);

    if (validateWebhooks(validationTargets)) {
      try {
        // We only execute deletion if all create/update operations were successful
        const shouldDelete = await tryPostCreateOrUpdateAndToastResult(validationTargets);

        if (shouldDelete && webhookIdsToDelete.current.length > 0) {
          const deleteSuccess = await tryDeleteWebhooksAndToastResult(webhookIdsToDelete.current);
          if (deleteSuccess) {
            webhookIdsToDelete.current = [];
          }
        }
      } finally {
        // refresh current webhook state from backend
        // NOTE: in cases of partial success, this will have the unfortunate side effect of clearing the user's input
        // for changes that could not be perisisted. Hopefully this will not be an issue.
        fetchWebhooks();
      }
    }
  };

  /** Posts new/updated webhooks and returns true if all updates were successful */
  const tryPostCreateOrUpdateAndToastResult = async (
    indexedWebhooks: IndexedSiteWebhookFormEntry[],
  ): Promise<boolean> => {
    const createOrUpdatePayload = indexedWebhooks.map(({ webhook }) => webhookFormEntryToPostObject(webhook));

    if (createOrUpdatePayload.length > 0) {
      const processingMessages = await webHookService.postSiteWebhooks(createOrUpdatePayload);
      processingMessages.forEach((pm) =>
        dispatch(
          createToast({
            type: pm.type.toLowerCase(),
            message: pm.content,
            timeout: pm.type === 'SUCCESS' ? TOAST_TIMEOUT : undefined,
          }),
        ),
      );
      return !processingMessages.some((pm) => pm.type !== 'SUCCESS');
    }

    return true;
  };

  const tryDeleteWebhooksAndToastResult = async (webhookIdsToDelete: number[]): Promise<boolean> => {
    const deleteSuccess = await webHookService.deleteSiteWebhooks(webhookIdsToDelete);
    if (deleteSuccess) {
      dispatch(
        createToast({
          type: 'success',
          message: `Deleted ${webhookIdsToDelete.length} site webhook(s)`,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return deleteSuccess;
  };

  /** Returns true if all webhook entries are valid */
  const validateWebhooks = (values: { index: number; webhook: SiteWebhookFormEntry }[]) => {
    const errors: WebhookFormErrors = {};

    for (const { index, webhook } of values) {
      const rowErrors = validateWebhook(webhook);
      if (Object.keys(rowErrors).length > 0) {
        // not the greatest implementation... have to delete all errors when we remove rows.
        // can use lodash uniqueid to give each row a uid to use for error keys if this becomes an issue
        errors[index] = rowErrors;
      }
    }

    setErrors(errors);
    return Object.keys(errors).length < 1;
  };

  const initializeWebhookFormEntries = () => {
    const formEntryRows: SiteWebhookFormEntry[] = loadedSiteWebhooks.map(
      // discard unused properties returned by backend
      ({ editedBy: _, createdAt: ___, updatedAt: ____, ...webhook }) => webhook,
    );
    // if an event type has no entries, give it a blank row to render, blank rows are ignored on save
    for (const webhookEventId of WEBHOOK_EVENT_ID_LIST) {
      if (!formEntryRows.some((row) => row.webhookEventId === webhookEventId)) {
        formEntryRows.push({ webhookEventId, siteId: selectedSite.id });
      }
    }
    setSiteWebhookFormEntries(formEntryRows);
  };

  const addWebhook = (webhookEventId: WEBHOOK_EVENT_IDS) => {
    const newWebhook: SiteWebhookFormEntry = { webhookEventId, siteId: selectedSite.id };
    setSiteWebhookFormEntries([...siteWebhookFormEntries, newWebhook]);
  };

  const updateWebhook = (webhookIndex: number, update: SiteWebhookFormEntry) => {
    const existingValue = siteWebhookFormEntries[webhookIndex];
    const newValue: SiteWebhookFormEntry = { ...existingValue, ...update, newOrUpdated: true };
    setSiteWebhookFormEntries([
      ...siteWebhookFormEntries.slice(0, webhookIndex),
      newValue,
      ...siteWebhookFormEntries.slice(webhookIndex + 1),
    ]);
  };

  const removeWebhook = (webhookIndex: number) => {
    const removedWebhook = siteWebhookFormEntries[webhookIndex];
    if (removedWebhook.id !== undefined) {
      // Webhook came from backend and needs an API call to persist deletion
      webhookIdsToDelete.current.push(removedWebhook.id);
    }
    setSiteWebhookFormEntries([
      ...siteWebhookFormEntries.slice(0, webhookIndex),
      ...siteWebhookFormEntries.slice(webhookIndex + 1),
    ]);
    setErrors({});
  };

  // when only one row remains, the Remove button clears its values so there is always one row rendered
  const clearWebhook = (webhookIndex: number) => {
    const removedWebhook = siteWebhookFormEntries[webhookIndex];
    if (removedWebhook.id !== undefined) {
      // Webhook came from backend and needs an API call to persist deletion
      webhookIdsToDelete.current.push(removedWebhook.id);
    }
    updateWebhook(webhookIndex, { webhookUrl: undefined, accommodationTypeId: undefined, id: undefined });
    setErrors({});
  };

  const selectableAcommodationTypes = Object.entries(ACCOMMODATION_TYPE_IDS)
    .filter(([_, value]) => typeof value === 'number') // enums are double mapped, exclude the reverse mapping string values
    .filter(
      ([_, value]) =>
        ![ACCOMMODATION_TYPE_IDS.BALI, ACCOMMODATION_TYPE_IDS.DALI].includes(value as ACCOMMODATION_TYPE_IDS),
    );
  if (selectedSite.countryId === COUNTRY_ID_MAPPING.USA_COUNTRY_ID) {
    selectableAcommodationTypes.push(['DALI', ACCOMMODATION_TYPE_IDS.DALI]);
  }
  if (selectedSite.countryId === COUNTRY_ID_MAPPING.CANADA_COUNTRY_ID) {
    selectableAcommodationTypes.push(['BALI', ACCOMMODATION_TYPE_IDS.BALI]);
  }
  const accommodationTypeSelectOptions: SelectOptionProps[] = selectableAcommodationTypes.map(([name, value]) => {
    return { label: name, value: value, key: value };
  });

  return (
    <LoadingSpinnerWrapper showSpinner={showLoadingSpinner} centerOfScreen={true}>
      {/* Row centers content */}
      <Row alignmentHorizontal="center" className="site-webhooks">
        <ResponsiveWidthBox spacingInset="400" xs="95%" md="85%" lg="75%">
          {/* Header */}
          <Row alignmentHorizontal="justify" widths={['fill', 'fit']}>
            <Heading level={1} type="h500">
              Webhooks for site {selectedSite.site}
            </Heading>
            {Object.keys(errors).length > 0 && (
              <Text type="b200" color="error">
                Please fix highlighted form errors to save.
              </Text>
            )}
            {isEditing ? (
              <>
                <Button onClick={toggleEditing} type="secondary">
                  Cancel
                </Button>
                <Button onClick={onSave}>Save</Button>
              </>
            ) : (
              <Button onClick={toggleEditing}>Edit</Button>
            )}
          </Row>
          {/* Main Content */}
          {WEBHOOK_EVENT_ID_LIST.map<React.ReactNode>((webhookEventId) => {
            // props assignment is a separate expression for better type checking
            const eventCardProps: WebhookEventCardProps = {
              webhookEventId,
              isEditing,
              accommodationTypeSelectOptions,
              loadedSiteWebhooks,
              siteWebhookFormEntries,
              errors,
              addWebhook,
              updateWebhook,
              removeWebhook,
              clearWebhook,
            };
            return <WebhookEventCard {...eventCardProps} key={webhookEventId} />;
          })}
        </ResponsiveWidthBox>
      </Row>
    </LoadingSpinnerWrapper>
  );
};

export default SiteWebHooks;
